import { Observer, observer } from 'mobx-react-lite'
import { Select, type SelectProps } from '@/components/inputs/Select'
import { bindSelect } from '@/infra/view-binding'
import type { FilterProps } from '@/infra/type-utils'
import {
  type ContainerProps,
  FormFieldContainer,
} from '@/components/FormContainer'

/**
 * Form field container for a {@link Select}.
 */
export const SelectContainer = observer(function SelectContainer<
  O extends object,
  M extends object,
  K extends keyof FilterProps<M, string | null>,
>({
  name,
  model,
  label,
  hint,
  field,
  error,
  hintTestId,
  'aria-label': ariaLabel,
  items,
  getKey,
  getLabel,
  ...rest
}: ContainerProps<M, string | null, K, Omit<SelectProps<O>, 'value'>>) {
  return (
    <FormFieldContainer
      name={name}
      model={model}
      label={label}
      hint={hint}
      field={field}
      error={error}
      aria-label={ariaLabel}
      hintTestId={hintTestId}
      render={(controlProps) => (
        // Using `Observer` here makes it so only the `Select`
        // re-renders when the value changes.
        <Observer>
          {() => (
            <Select<O>
              {...controlProps}
              {...bindSelect(model, field)}
              {...rest}
              items={items}
              getKey={getKey}
              getLabel={getLabel}
            />
          )}
        </Observer>
      )}
    />
  )
})
